/**
 * Contenu pédagogique public d’une filière (saisi côté admin).
 * Inspiré d’un programme type : technologies + modules (titre + liste d’items).
 */
export type FiliereContenuModule = {
  title: string;
  items: string[];
};

export type FiliereContenuPedagogique = {
  programmeIntro?: string | null;
  technologiesLabel?: string | null;
  technologies: string[];
  modules: FiliereContenuModule[];
};

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
}

export function parseFiliereContenuPedagogique(raw: unknown): FiliereContenuPedagogique | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const input = raw as Record<string, unknown>;
  const technologies = asStringArray(input.technologies);
  const technologiesLabel =
    typeof input.technologiesLabel === 'string' ? input.technologiesLabel.trim() || null : null;
  const programmeIntro =
    typeof input.programmeIntro === 'string' ? input.programmeIntro.trim() || null : null;

  const modules: FiliereContenuModule[] = [];
  if (Array.isArray(input.modules)) {
    for (const row of input.modules) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
      const m = row as Record<string, unknown>;
      const title = typeof m.title === 'string' ? m.title.trim() : '';
      if (!title) continue;
      modules.push({ title, items: asStringArray(m.items) });
    }
  }

  if (!technologies.length && !modules.length && !programmeIntro) return null;
  return { programmeIntro, technologiesLabel, technologies, modules };
}

export function hasFiliereContenuPedagogique(raw: unknown): boolean {
  const parsed = parseFiliereContenuPedagogique(raw);
  if (!parsed) return false;
  return parsed.modules.length > 0 || parsed.technologies.length > 0;
}

export function emptyFiliereContenuPedagogique(): FiliereContenuPedagogique {
  return {
    programmeIntro: '',
    technologiesLabel: 'Technologies abordées',
    technologies: [],
    modules: [],
  };
}
